
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface Breadcrumb {
  path: string;
  title: string;
  timestamp: number;
  params?: Record<string, string>;
  contextData?: any;
}

interface NavigationState {
  canGoBack: boolean;
  canGoForward: boolean;
  depth: number;
  currentIndex: number;
}

interface NavigationContextType {
  breadcrumbs: Breadcrumb[];
  navigationState: NavigationState;
  pushBreadcrumb: (path: string, title: string, params?: Record<string, string>, contextData?: any) => void;
  goBack: () => void;
  goForward: () => void;
  clearBreadcrumbs: () => void;
  getBackPath: () => string | null;
  getBackTitle: () => string | null;
  getNavigationDepth: () => number;
  persistNavigation: () => void;
  restoreNavigation: () => void;
  error: Error | null;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    console.error('❌ useNavigation must be used within a NavigationProvider');
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

const NAVIGATION_STORAGE_KEY = 'luvall_navigation_state';
const MAX_BREADCRUMBS = 20; // Industry standard for navigation depth

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const pushBreadcrumb = useCallback((path: string, title: string, params?: Record<string, string>, contextData?: any) => {
    try {
      if (!path || !title) {
        console.warn('⚠️ NavigationContext: Invalid breadcrumb data provided');
        return;
      }

      setBreadcrumbs(prev => {
        // Don't add duplicate consecutive breadcrumbs
        if (prev.length > 0 && prev[prev.length - 1].path === path) {
          return prev;
        }
        
        // Remove any breadcrumbs after current index (for forward navigation)
        const currentBreadcrumbs = prev.slice(0, currentIndex + 1);
        
        // Add new breadcrumb
        const newBreadcrumbs = [...currentBreadcrumbs, { 
          path, 
          title, 
          timestamp: Date.now(),
          params,
          contextData 
        }];
        
        // Limit breadcrumbs to industry standard
        const limitedBreadcrumbs = newBreadcrumbs.slice(-MAX_BREADCRUMBS);
        
        // Update current index
        setCurrentIndex(limitedBreadcrumbs.length - 1);
        
        return limitedBreadcrumbs;
      });
    } catch (err) {
      console.error('❌ NavigationContext: Error pushing breadcrumb:', err);
      setError(err instanceof Error ? err : new Error('Failed to add breadcrumb'));
    }
  }, [currentIndex]);

  const getBackPath = useCallback(() => {
    try {
      if (currentIndex > 0 && breadcrumbs[currentIndex - 1]) {
        return breadcrumbs[currentIndex - 1].path;
      }
      return null;
    } catch (err) {
      console.error('❌ NavigationContext: Error getting back path:', err);
      setError(err instanceof Error ? err : new Error('Failed to get back path'));
      return null;
    }
  }, [breadcrumbs, currentIndex]);

  const getBackTitle = useCallback(() => {
    try {
      if (currentIndex > 0 && breadcrumbs[currentIndex - 1]) {
        return breadcrumbs[currentIndex - 1].title;
      }
      return null;
    } catch (err) {
      console.error('❌ NavigationContext: Error getting back title:', err);
      return null;
    }
  }, [breadcrumbs, currentIndex]);

  const getNavigationDepth = useCallback(() => {
    return breadcrumbs.length;
  }, [breadcrumbs]);

  const goBack = useCallback(() => {
    try {
      const backPath = getBackPath();
      if (backPath && currentIndex > 0) {
        // Update current index to go back
        setCurrentIndex(prev => prev - 1);
        navigate(backPath);
      } else {
        // Fallback to browser back
        navigate(-1);
      }
    } catch (err) {
      console.error('❌ NavigationContext: Error going back:', err);
      setError(err instanceof Error ? err : new Error('Failed to navigate back'));
      // Fallback to browser back
      try {
        navigate(-1);
      } catch (fallbackErr) {
        console.error('❌ NavigationContext: Fallback navigation also failed:', fallbackErr);
      }
    }
  }, [getBackPath, navigate, currentIndex]);

  const goForward = useCallback(() => {
    try {
      if (currentIndex < breadcrumbs.length - 1) {
        const forwardPath = breadcrumbs[currentIndex + 1].path;
        setCurrentIndex(prev => prev + 1);
        navigate(forwardPath);
      }
    } catch (err) {
      console.error('❌ NavigationContext: Error going forward:', err);
      setError(err instanceof Error ? err : new Error('Failed to navigate forward'));
    }
  }, [breadcrumbs, currentIndex, navigate]);

  const clearBreadcrumbs = useCallback(() => {
    try {
      setBreadcrumbs([]);
      setCurrentIndex(0);
      setError(null);
      localStorage.removeItem(NAVIGATION_STORAGE_KEY);
    } catch (err) {
      console.error('❌ NavigationContext: Error clearing breadcrumbs:', err);
      setError(err instanceof Error ? err : new Error('Failed to clear breadcrumbs'));
    }
  }, []);

  const persistNavigation = useCallback(() => {
    try {
      const navigationData = {
        breadcrumbs,
        currentIndex,
        timestamp: Date.now()
      };
      localStorage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(navigationData));
    } catch (err) {
      console.error('❌ NavigationContext: Error persisting navigation:', err);
    }
  }, [breadcrumbs, currentIndex]);

  const restoreNavigation = useCallback(() => {
    try {
      const stored = localStorage.getItem(NAVIGATION_STORAGE_KEY);
      if (stored) {
        const navigationData = JSON.parse(stored);
        const now = Date.now();
        // Only restore if less than 1 hour old
        if (now - navigationData.timestamp < 3600000) {
          setBreadcrumbs(navigationData.breadcrumbs || []);
          setCurrentIndex(navigationData.currentIndex || 0);
        } else {
          localStorage.removeItem(NAVIGATION_STORAGE_KEY);
        }
      }
    } catch (err) {
      console.error('❌ NavigationContext: Error restoring navigation:', err);
      localStorage.removeItem(NAVIGATION_STORAGE_KEY);
    }
  }, []);

  // Navigation state calculation
  const navigationState: NavigationState = {
    canGoBack: currentIndex > 0,
    canGoForward: currentIndex < breadcrumbs.length - 1,
    depth: breadcrumbs.length,
    currentIndex
  };

  // Persist navigation on changes
  React.useEffect(() => {
    if (breadcrumbs.length > 0) {
      persistNavigation();
    }
  }, [breadcrumbs, currentIndex, persistNavigation]);

  // Restore navigation on mount
  React.useEffect(() => {
    restoreNavigation();
  }, [restoreNavigation]);

  return (
    <NavigationContext.Provider value={{
      breadcrumbs,
      navigationState,
      pushBreadcrumb,
      goBack,
      goForward,
      clearBreadcrumbs,
      getBackPath,
      getBackTitle,
      getNavigationDepth,
      persistNavigation,
      restoreNavigation,
      error
    }}>
      {children}
    </NavigationContext.Provider>
  );
};
